import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, PageContainer, Section } from "@swot/base-components";
import { 
  ShieldAlert, 
  Timer, 
  History, 
  Activity, 
  Search, 
  ShieldCheck, 
  UserCheck, 
  FileSearch, 
  ChevronRight,
  Info
} from "lucide-react";
import { CmtsApiErrorPanel } from "../CmtsApiErrorPanel.js";
import { CmtsAllowedActionsPanel } from "./CmtsAllowedActionsPanel.js";
import { CmtsComplaintLifecyclePanel } from "./CmtsComplaintLifecyclePanel.js";
import { CmtsComplaintTimeline } from "./CmtsComplaintTimeline.js";
import { CmtsWorkflowActionDialog } from "./CmtsWorkflowActionDialog.js";
import { complaintApi } from "../../services/complaintApi.js";
import { createIdempotencyKey } from "../../services/api.service.js";
import { normalizeComplaintUiError } from "../../core/errors/cmtsComplaintErrorNormalizer.js";
import type { ComplaintAllowedActionDto, ComplaintWorkflowActionRequest } from "../../../types/complaint.types.js";
import { invalidateCmtsLifecycleMutation } from "../../core/query/cmtsLifecycleInvalidation.js";

// Define the allowed section types to satisfy TypeScript
type VisibleSection = "OVERVIEW" | "RCA" | "CAPA" | "VERIFICATION" | "EVIDENCE" | "MESSAGES" | "TASKS" | "TIMELINE" | "AUDIT";

function useComplaintIdFromRoute(): string {
  const params = useParams<{ complaintId: string }>();
  return params.complaintId ?? "";
}

export function CmtsComplaintDetailShell() {
  const complaintId = useComplaintIdFromRoute();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAction, setSelectedAction] = useState<ComplaintAllowedActionDto | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const detailQuery = useQuery({ 
    queryKey: ["cmts", "complaint-detail-backend", complaintId], 
    queryFn: ({ signal }) => complaintApi.get(complaintId, signal), 
    enabled: Boolean(complaintId), 
    retry: 1 
  });
  
  const actionsQuery = useQuery({ 
    queryKey: ["cmts", "complaint-allowed-actions-backend", complaintId], 
    queryFn: ({ signal }) => complaintApi.getAllowedActions(complaintId, signal), 
    enabled: Boolean(complaintId), 
    retry: 1 
  });
  
  // Cast the data to the correct Set type
  const visibleSections = new Set<VisibleSection>((detailQuery.data?.visibleSections as VisibleSection[]) ?? []);
  
  const timelineQuery = useQuery({ 
    queryKey: ["cmts", "complaint-timeline-backend", complaintId], 
    queryFn: ({ signal }) => complaintApi.getTimeline(complaintId, signal), 
    enabled: Boolean(complaintId && visibleSections.has("TIMELINE")), 
    retry: 1 
  });

 const workflowMutation =
  useMutation({
    mutationFn: async ({
      actionCode,
      payload,
    }: {
      actionCode: string;
      payload:
        ComplaintWorkflowActionRequest;
    }) =>
      complaintApi
        .executeWorkflowAction(
          complaintId,
          actionCode,
          payload,
          createIdempotencyKey(
            `complaint-workflow-${actionCode}`,
          ),
        ),

    onSuccess: async () => {
      // Dialog மற்றும் error state-ஐ
      // முதலில் clear செய்யவும்.
      setSelectedAction(null);
      setActionError(null);

      await invalidateCmtsLifecycleMutation(
        queryClient,
        complaintId,
        "complaint",
      );

      // Current screen queries-ஐ
      // direct-aa refetch செய்யவும்.
      await Promise.allSettled([
        detailQuery.refetch(),

        actionsQuery.refetch(),

        visibleSections.has(
          "TIMELINE",
        )
          ? timelineQuery.refetch()
          : Promise.resolve(),

        queryClient.invalidateQueries({
          queryKey: [
            "cmts",
            "backend-complaints",
          ],
        }),
      ]);
    },

    onError: (
      error: unknown,
    ) => {
      setActionError(error);
    },
  });

  if (!complaintId) {
    return (
      <PageContainer title="Complaint detail">
        <CmtsApiErrorPanel 
          error={new Error("complaintId is required for complaint detail.")} 
          title="Missing complaint id" 
          className="rounded-2xl border-none shadow-lg"
        />
      </PageContainer>
    );
  }

  
  const actionUiError = actionError ? normalizeComplaintUiError(actionError, "Workflow action failed.") : null;
  const record = detailQuery.data ?? null;

  return (
    <PageContainer 
      title={"Complaint Detail"} 
      className="bg-slate-500/50 font-bold min-h-screen pb-12"
    >
      {/* Error Panels */}
      <div className="space-y-4 mb-6">
        {detailQuery.error && (
          <CmtsApiErrorPanel 
            error={detailQuery.error} 
            title="Backend complaint detail failed" 
            onRetry={() => void detailQuery.refetch()} 
            className="rounded-2xl border-none shadow-md"
          />
        )}
        {actionsQuery.error && (
          <CmtsApiErrorPanel 
            error={actionsQuery.error} 
            title="Allowed actions failed" 
            onRetry={() => void actionsQuery.refetch()} 
            className="rounded-2xl border-none shadow-md"
          />
        )}
        {actionUiError && (
          <CmtsApiErrorPanel 
            error={actionError} 
            title={actionUiError.userMessage} 
            className="rounded-2xl border-none shadow-md"
          />
        )}
      </div>

      {/* Modern Header Card */}
      <div className="mb-8 rounded-[2rem] bg-white p-8 shadow-xl shadow-slate-200/50 animate-in fade-in slide-in-from-top-4 duration-700">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                Lifecycle & Operations
              </span>
            </div>
            <h1 className="text-4xl font-black tracking-tight text-slate-900">
              {record?.complaintCode ?? "Complaint"}
            </h1>
            <p className="text-slate-500 font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-400" />
              Complaint Lifecycle Management
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {record?.priorityName && (
              <div className="flex items-center gap-2 rounded-2xl bg-rose-50 px-5 py-2.5 text-sm font-bold text-rose-600 border border-rose-100/50 shadow-sm">
                <ShieldAlert className="h-4 w-4" />
                {record.priorityName}
              </div>
            )}
            {record?.slaStateName && (
              <div className="flex items-center gap-2 rounded-2xl bg-amber-50 px-5 py-2.5 text-sm font-bold text-amber-600 border border-amber-100/50 shadow-sm">
                <Timer className="h-4 w-4" />
                {record.slaStateName}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="grid gap-8 lg:grid-cols-3">
        {/* Left Column: Lifecycle & Timeline */}
        <div className="lg:col-span-2 space-y-8 animate-in fade-in slide-in-from-left-4 duration-700 delay-100">
          <CmtsComplaintLifecyclePanel record={record} />
          
          {/* Modules Section */}
          <div className="rounded-[2rem] bg-white p-8 shadow-lg shadow-slate-200/40 border border-slate-50">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <Activity className="h-5 w-5" />
                </div>
                <h2 className="text-xl font-bold text-slate-800">Linked Modules</h2>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { id: "RCA" as VisibleSection, label: "RCA", icon: Search, path: "rca", color: "text-blue-600", bg: "bg-blue-50" },
                { id: "CAPA" as VisibleSection, label: "CAPA", icon: ShieldCheck, path: "capa", color: "text-emerald-600", bg: "bg-emerald-50" },
                { id: "VERIFICATION" as VisibleSection, label: "Verification", icon: UserCheck, path: "verification", color: "text-purple-600", bg: "bg-purple-50" },
                { id: "EVIDENCE" as VisibleSection, label: "Evidence", icon: FileSearch, path: "evidence", color: "text-amber-600", bg: "bg-amber-50" },
              ].map((module) => (
                visibleSections.has(module.id) && (
                  <button
                    key={module.id}
                    onClick={() => navigate(`/cmts/complaints/${encodeURIComponent(complaintId)}/${module.path}`)}
                    className="group flex flex-col items-center gap-3 rounded-2xl bg-white p-6 border border-slate-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-indigo-100"
                  >
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${module.bg} ${module.color} transition-transform group-hover:scale-110`}>
                      <module.icon className="h-6 w-6" />
                    </div>
                    <span className="text-sm font-bold text-slate-700">{module.label}</span>
                    <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                  </button>
                )
              ))}
            </div>
          </div>

          {/* Timeline Section */}
          {visibleSections.has("TIMELINE") && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
              <div className="flex items-center gap-3 mb-6 px-4">
                <History className="h-5 w-5 text-slate-400" />
                <h3 className="text-lg font-bold text-slate-700">Complaint History</h3>
              </div>
              <CmtsComplaintTimeline items={timelineQuery.data ?? []} />
            </div>
          )}
        </div>

        {/* Right Column: Actions & Backend State */}
        <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-700 delay-200">
         <CmtsAllowedActionsPanel
  actions={
    actionsQuery.data ?? []
  }
  busyActionCode={
    workflowMutation.isPending
      ? workflowMutation
          .variables
          ?.actionCode
      : undefined
  }
  onAction={async (
    _code,
    action,
  ) => {
    setActionError(null);
    setSelectedAction(action);
  }}
/>

          {/* Backend State Card */}
          <div className="rounded-[2rem] bg-indigo-900 p-8 text-white shadow-2xl shadow-indigo-200 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
            <div className="relative z-10 space-y-4">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-white/20 p-2">
                  <Info className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold">State Reload Contract</h3>
              </div>
              <p className="text-sm leading-relaxed text-indigo-100 font-medium">
                Workflow actions are executed only after backend validation. 
                Queries are auto-refreshed to ensure UI reflects the latest state.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Dialog */}
      <CmtsWorkflowActionDialog
        action={selectedAction}
        rowVersion={record?.rowVersion}
        busy={workflowMutation.isPending}
        error={actionUiError?.userMessage ?? null}
        onCancel={() => setSelectedAction(null)}
        onConfirm={async (payload) => {
          if (!selectedAction?.actionCode) throw new Error("Backend allowed action is missing actionCode.");
          await workflowMutation.mutateAsync({ actionCode: selectedAction.actionCode, payload });
        }}
      />
    </PageContainer>
  );
}
